import * as Highcharts from 'highcharts'
import { useEffect, useRef, type RefObject } from 'react'

import { hideChartSkeleton } from '@/shared/chartSkeleton'

export type ChartHandle = {
	update: (next: Highcharts.Options) => void
}

const FALLBACK_HEIGHT_PX = 400

/**
 * Compute the height/width the chart should fill inside its block.
 *
 * Mirrors `assets/js/modules/charts.ts::fixChartSize` for parity with PHP-rendered charts:
 * find the enclosing block container, subtract header/footer/title areas, leave the rest
 * for the chart. Width is the block's content-box width (offsetWidth minus horizontal padding).
 */
const computeChartSize = (container: HTMLElement): { width: number; height: number } | null => {
	const block = container.closest('.js-resize-item-block') as HTMLElement | null
	if (block === null) {
		return null
	}
	const header = block.querySelector<HTMLElement>('.js-resize-item-head')
	const footer = block.querySelector<HTMLElement>('.js-resize-item-footer')
	const minusEls = block.querySelectorAll<HTMLElement>('.js-resize-height-minus')

	const headerH = header?.offsetHeight ?? 0
	const footerH = footer?.offsetHeight ?? 0
	let minusH = 0
	minusEls.forEach((el) => {
		minusH += el.offsetHeight
	})

	const { paddingLeft, paddingRight } = window.getComputedStyle(block)
	const padX = (parseFloat(paddingLeft) || 0) + (parseFloat(paddingRight) || 0)

	const height = Math.max(block.offsetHeight - headerH - footerH - minusH, FALLBACK_HEIGHT_PX)
	const width = Math.max(block.offsetWidth - padX, 0)
	return { width, height }
}

export const useHighchartsInstance = (
	containerRef: RefObject<HTMLDivElement | null>,
	initialOptions: Highcharts.Options,
): ChartHandle => {
	const chartRef = useRef<Highcharts.Chart | null>(null)

	useEffect(() => {
		if (containerRef.current === null) {
			return undefined
		}

		chartRef.current = Highcharts.chart(containerRef.current, initialOptions)
		hideChartSkeleton(chartRef.current.container as HTMLElement | null)

		const resize = (): void => {
			if (chartRef.current === null || containerRef.current === null) {
				return
			}
			const size = computeChartSize(containerRef.current)
			if (size !== null) {
				chartRef.current.update({ chart: { width: size.width, height: size.height } }, true, false, false)
			}
		}

		resize()
		window.addEventListener('resize', resize)

		return () => {
			window.removeEventListener('resize', resize)
			chartRef.current?.destroy()
			chartRef.current = null
		}
		// initialOptions intentionally captured once on mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return {
		update: (next) => {
			chartRef.current?.update(next, true, true)
		},
	}
}
